import { toast } from "sonner";
import { useContext } from "react";
import { CrmProvider, AuthMethod, CrmFormData } from "../types";
import AuthMethodSelector from "../AuthMethodSelector";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

type PipedriveAuthConfigurationProps = {
  formData: CrmFormData;
  onFormDataChange: (data: Partial<CrmFormData>) => void;
  hasConnection: boolean;
  crmConnection: any;
};

const PipedriveAuthConfiguration = ({
  formData,
  onFormDataChange,
  hasConnection,
  crmConnection,
}: PipedriveAuthConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const utils = trpc.useUtils();
  const createConnection = trpc.crmSelfConfig.create.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al crear la conexión.",
      });
    },
  });

  const testConnection = trpc.crmSelfConfig.testConnection.useMutation({
    onSuccess: async (result) => {
      if (result.success) {
        const apiKey = formData.apiKey || crmConnection?.pipedrive?.apiKey;

        if (merchantId) {
          await createConnection.mutateAsync({
            merchantId,
            provider: "pipedrive" as CrmProvider,
            pipedrive: apiKey ? { apiKey: apiKey || "" } : undefined,
            status: "success",
            enabled: true,
          });
        }

        toast.success("Conexión exitosa", {
          description: "La conexión con el CRM se ha establecido correctamente.",
        });
      } else {
        toast.error("Error de conexión", {
          description: result.error || "No se pudo establecer la conexión con el CRM.",
        });
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrió un error al probar la conexión.",
      });
    },
  });

  const handleTestConnection = () => {
    const apiKey = formData.apiKey || crmConnection?.pipedrive?.apiKey;

    if (!apiKey) {
      toast.warning("API Key requerida", {
        description: "Por favor ingresa tu API Key antes de probar la conexión.",
      });
      return;
    }

    testConnection.mutate({
      provider: "pipedrive" as CrmProvider,
      apiKey: apiKey || undefined,
    });
  };

  const handleAuthMethodChange = (method: AuthMethod) => {
    onFormDataChange({
      authMethod: method,
      apiKey: formData.authMethod === method ? formData.apiKey : "",
    });
  };

  const handleApiKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ apiKey: e.target.value });
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-lg font-bold text-gray-700">
              Método de autenticación
            </Label>
            {hasConnection && crmConnection.status === "success" && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Conexión exitosa
              </span>
            )}
          </div>
          <AuthMethodSelector
            methods={["apiKey"]}
            selectedMethod={
              hasConnection && crmConnection.status === "success"
                ? "apiKey"
                : formData.authMethod || "apiKey"
            }
            onSelect={handleAuthMethodChange}
            isDisabled={hasConnection && crmConnection.status === "success"}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            API Key
          </Label>
          <Input
            type="password"
            value={
              hasConnection && crmConnection.status === "success" && crmConnection.pipedrive?.apiKey
                ? "••••••••••••"
                : formData.apiKey || crmConnection?.pipedrive?.apiKey || ""
            }
            onChange={handleApiKeyChange}
            placeholder="Ingresa tu API Key"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={hasConnection && crmConnection.status === "success"}
          />
        </div>
        {!(hasConnection && crmConnection.status === "success") && (
          <Button
            variant="main"
            onClick={handleTestConnection}
            disabled={testConnection.isLoading || (!formData.apiKey && !crmConnection?.pipedrive?.apiKey)}
          >
            {testConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
            Probar conexión
          </Button>
        )}
      </div>
    </div>
  );
};

export default PipedriveAuthConfiguration;
